import React from 'react'
import { Stack, Typography } from '@mui/material'
import { FormTable, FormTableRow } from './FormTable'
import {
    RegisteredDecimalField,
    RegisteredIntegerField,
    RegisteredNumericField,
} from '../../../components/fields/NumericField'
import { ShouldShow, ShouldShowAny, showField } from '../../../components/entities/IssueType'
import { RegisteredCheckBox } from '../../../components/fields/CheckBox'
import { useFormContext } from 'react-hook-form'
import { formatMoney } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { RegisteredTextField } from '../../../components/fields/TextField'
import { variableLabel } from './variableLabel'
import { FormIssueEntity } from '../types/FormIssueEntity'
import { usePressReleasesExtractions, type FieldProvenance } from './issue-form/PressReleasesExtractionsContext'
import ExtractionSuggestionControls from './issue-form/ExtractionSuggestionControls'

// Small helper that renders the AI suggestion UI for a term field.
// It normalizes provenance keys and shows the `ExtractionSuggestionControls` which
// provides a "Use" action and a source popover. Consumers should call the
// provided `onUse` handler to apply the suggested value into the form state.
function FieldExtractionSuggestion({
    label,
    fieldValue,
    value,
    onUse,
    provenance,
}: {
    label?: string
    value?: string | number | null
    fieldValue?: string | number | null
    onUse: () => void
    provenance?: FieldProvenance | null
}) {
    const suggestionText = value == null ? null : `AI förslag: ${label ? `${label} ` : ''}${value}`
    const sourcePages = provenance?.sourcePages ?? []
    const quote = provenance?.quote || null

    const formattedValue = value ? parseFloat(String(value)) : null
    const formattedFieldValue = fieldValue ? parseFloat(String(fieldValue)) : null

    return (
        <Stack spacing={0.5} sx={{ mt: 0.25 }}>
            <ExtractionSuggestionControls
                suggestionText={suggestionText}
                onUse={onUse}
                pressReleaseId={provenance?.pressReleaseId}
                sourcePages={sourcePages}
                quote={quote}
                warning={
                    formattedFieldValue !== null && formattedValue !== null && formattedFieldValue !== formattedValue
                        ? 'AI föreslår ett annat värde'
                        : null
                }
            />
        </Stack>
    )
}

function TermsFields({
    issue,
    noSecondaryOfferingChecked,
    setIsCustomFormDirty,
}: {
    issue: FormIssueEntity
    noSecondaryOfferingChecked: boolean
    setIsCustomFormDirty: (v: boolean) => void
}) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const formMethods = useFormContext()
    const { suggestionOfferingTerms, suggestionProvenanceByField } = usePressReleasesExtractions()
    const abbTarget = formMethods.watch('abbTarget')
    const offeredUnits = parseInt(formMethods.watch('offeredUnits')) || 0
    const unitSubPrice = parseFloat(formMethods.watch('unitSubPrice')) || 0
    const sharesInUnit = parseInt(formMethods.watch('sharesInUnit')) || 1
    const pricePerShare = unitSubPrice / sharesInUnit

    const pricePerShareIntervalLow = (parseFloat(formMethods.watch('ipoIntervalLow')) || 0) / sharesInUnit
    const pricePerShareIntervalHigh = (parseFloat(formMethods.watch('ipoIntervalHigh')) || 0) / sharesInUnit

    const totalOfferedUnits = offeredUnits + (parseInt(formMethods.watch('offeredUnitsUnlisted')) || 0)
    const issueSize = unitSubPrice * totalOfferedUnits
    const noOverAllotmentChecked = !!formMethods.watch('noOverAllotment')
    const rto = formMethods.watch('rto')
    const earnout = formMethods.watch('earnout')

    // Apply suggestion into react-hook-form so the suggestion behaves like an
    // ordinary user input (triggers dirty/touched and validation flows).
    const applySuggestion = (fieldName: string, value: any) => {
        if (value == null) return
        formMethods.setValue(fieldName, value, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        })
    }

    if (!issue) return null

    return (
        <FormTable>
            <ShouldShowAny
                issueType={issue.type}
                issueFields={['sharesRequired', 'rightsReceived', 'rightsRequired', 'unitsReceived']}
            >
                <FormTableRow label='Villkor'>
                    <Stack spacing={4}>
                        {!issue.termsDate && (
                            <RegisteredCheckBox
                                label='Teckningskurs offentliggörs senare'
                                name='noTermsInitially'
                                onChange={() => setIsCustomFormDirty(true)}
                            />
                        )}
                        <Stack spacing={4}>
                            <Stack spacing={0.5}>
                                <Stack
                                    direction='row'
                                    spacing={1}
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <RegisteredNumericField label='Antal aktier' name='sharesRequired' decimals={0} />
                                    <Typography variant='body2' sx={{ fontSize: 30 }}>
                                        ={'>'}
                                    </Typography>
                                    <RegisteredNumericField label='Antal rätter' name='rightsReceived' decimals={0} />
                                </Stack>
                                <Stack direction='row' spacing={1}>
                                    <FieldExtractionSuggestion
                                        value={suggestionOfferingTerms.sharesRequired}
                                        fieldValue={formMethods.watch('sharesRequired')}
                                        onUse={() =>
                                            applySuggestion('sharesRequired', suggestionOfferingTerms.sharesRequired)
                                        }
                                        provenance={suggestionProvenanceByField.sharesRequired}
                                    />
                                    <Typography variant='body2' sx={{ fontSize: 30, visibility: 'hidden' }}>
                                        ={'>'}
                                    </Typography>
                                    <FieldExtractionSuggestion
                                        value={suggestionOfferingTerms.rightsReceived}
                                        fieldValue={formMethods.watch('rightsReceived')}
                                        onUse={() =>
                                            applySuggestion('rightsReceived', suggestionOfferingTerms.rightsReceived)
                                        }
                                        provenance={suggestionProvenanceByField.rightsReceived}
                                    />
                                </Stack>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Stack
                                    direction='row'
                                    spacing={1}
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <RegisteredNumericField label='Antal rätter' name='rightsRequired' decimals={0} />
                                    <Typography variant='body2' sx={{ fontSize: 30 }}>
                                        ={'>'}
                                    </Typography>
                                    <RegisteredNumericField
                                        label={variableLabel('unitsReceived', issue)}
                                        name='unitsReceived'
                                        decimals={0}
                                    />
                                </Stack>
                                <Stack direction='row' spacing={1}>
                                    <FieldExtractionSuggestion
                                        value={suggestionOfferingTerms.rightsRequired}
                                        fieldValue={formMethods.watch('rightsRequired')}
                                        onUse={() =>
                                            applySuggestion('rightsRequired', suggestionOfferingTerms.rightsRequired)
                                        }
                                        provenance={suggestionProvenanceByField.rightsRequired}
                                    />
                                    <Typography variant='body2' sx={{ fontSize: 30, visibility: 'hidden' }}>
                                        ={'>'}
                                    </Typography>
                                    <FieldExtractionSuggestion
                                        value={suggestionOfferingTerms.unitsReceived}
                                        fieldValue={formMethods.watch('unitsReceived')}
                                        onUse={() =>
                                            applySuggestion('unitsReceived', suggestionOfferingTerms.unitsReceived)
                                        }
                                        provenance={suggestionProvenanceByField.unitsReceived}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </FormTableRow>
            </ShouldShowAny>
            <ShouldShow issueType={issue.type} issueField='sharesInUnit'>
                <FormTableRow label='Aktier per unit'>
                    <Stack spacing={0.5}>
                        <Stack
                            direction='row'
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <RegisteredNumericField label='Aktier per unit' name='sharesInUnit' decimals={0} />
                            <RegisteredTextField
                                name='unitExtraContent'
                                sx={{ width: '100%' }}
                                label='Extraordinärt innehåll i unit (inte TO)'
                                multiline
                                minRows={1}
                            />
                        </Stack>
                        <FieldExtractionSuggestion
                            value={suggestionOfferingTerms.sharesInUnit}
                            fieldValue={formMethods.watch('sharesInUnit')}
                            onUse={() => applySuggestion('sharesInUnit', suggestionOfferingTerms.sharesInUnit)}
                            provenance={suggestionProvenanceByField.sharesInUnit}
                        />
                    </Stack>
                </FormTableRow>
            </ShouldShow>
            <ShouldShowAny issueType={issue.type} issueFields='ipoIntervalLow, ipoIntervalHigh'>
                <FormTableRow label='Pris per unit (intervall)'>
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'flex-start',
                        }}
                    >
                        <RegisteredDecimalField label={variableLabel('ipoIntervalLow', issue)} name='ipoIntervalLow' />
                        <RegisteredDecimalField
                            label={variableLabel('ipoIntervalHigh', issue)}
                            name='ipoIntervalHigh'
                        />
                        <Typography variant='body2' sx={{ mt: 2 }}>
                            {`Pris per aktie:
                            ${formatMoney(pricePerShareIntervalLow, {
                                currencySymbol: issue.currency,
                                minDecimals: 2,
                                maxDecimals: 5,
                                locale,
                            })}
                             -
                            ${formatMoney(pricePerShareIntervalHigh, {
                                currencySymbol: issue.currency,
                                minDecimals: 2,
                                maxDecimals: 5,
                                locale,
                            })}`}
                        </Typography>
                    </Stack>
                </FormTableRow>
            </ShouldShowAny>
            <ShouldShow issueType={issue.type} issueField='unitSubPrice'>
                <FormTableRow label={variableLabel('unitSubPrice', issue)}>
                    <Stack spacing={0.5}>
                        <Stack
                            direction='row'
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <RegisteredDecimalField label={variableLabel('unitSubPrice', issue)} name='unitSubPrice' />
                            <Typography variant='body2'>
                                Pris per aktie:{' '}
                                {formatMoney(pricePerShare, {
                                    currencySymbol: issue.currency,
                                    minDecimals: 2,
                                    maxDecimals: 5,
                                    locale,
                                })}
                            </Typography>
                        </Stack>
                        <FieldExtractionSuggestion
                            value={suggestionOfferingTerms.unitSubPrice}
                            fieldValue={formMethods.watch('unitSubPrice')}
                            onUse={() => applySuggestion('unitSubPrice', suggestionOfferingTerms.unitSubPrice)}
                            provenance={suggestionProvenanceByField.unitSubPrice}
                        />
                    </Stack>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='offeredUnits'>
                <FormTableRow
                    label={variableLabel('offeredUnits', issue)}
                    hasNoBorderBottom={showField(issue.type, 'offeredUnitsUnlisted')}
                >
                    <Stack spacing={0.5}>
                        <Stack
                            direction='row'
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <RegisteredIntegerField
                                label={variableLabel('offeredUnits', issue)}
                                name='offeredUnits'
                                disabled={!!abbTarget}
                            />
                            <Typography variant='body2'>
                                Emissionsstorlek:{' '}
                                {formatMoney(issueSize, {
                                    currencySymbol: issue.currency,
                                    minDecimals: 0,
                                    maxDecimals: 5,
                                    locale,
                                })}
                            </Typography>
                        </Stack>
                        <FieldExtractionSuggestion
                            value={suggestionOfferingTerms.offeredUnits}
                            fieldValue={formMethods.watch('offeredUnits')}
                            onUse={() => applySuggestion('offeredUnits', suggestionOfferingTerms.offeredUnits)}
                            provenance={suggestionProvenanceByField.offeredUnits}
                        />
                    </Stack>
                    <ShouldShow issueType={issue.type} issueField='spac'>
                        <RegisteredCheckBox
                            label='SPAC'
                            name='spac'
                            disabled={rto}
                            onChange={() => {
                                setIsCustomFormDirty(true)
                            }}
                        />
                    </ShouldShow>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='offeredUnitsUnlisted'>
                <FormTableRow label='Antal units i onoterade aktieslag'>
                    <RegisteredIntegerField
                        label='Antal units'
                        name='offeredUnitsUnlisted'
                        disabled={false}
                        sx={{ marginRight: 4 }}
                    />
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='noncashConsideration'>
                <FormTableRow label='Apportegendom'>
                    <RegisteredTextField
                        label='Apportegendom'
                        name='noncashConsideration'
                        fullWidth
                        multiline
                        minRows={3}
                    />

                    <ShouldShow issueType={issue.type} issueField='earnout'>
                        <Stack
                            direction='row'
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <RegisteredCheckBox
                                label='Tilläggsköpeskilling'
                                name='earnout'
                                disabled={rto}
                                onChange={() => {
                                    setIsCustomFormDirty(true)
                                }}
                            />
                        </Stack>
                    </ShouldShow>
                    <ShouldShow issueType={issue.type} issueField='rto'>
                        <Stack
                            direction='row'
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <RegisteredCheckBox
                                label='Omvänt förvärv (RTO)'
                                name='rto'
                                disabled={earnout}
                                onChange={() => {
                                    setIsCustomFormDirty(true)
                                }}
                            />
                        </Stack>
                    </ShouldShow>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='abbTarget'>
                <FormTableRow label='Cirka belopp'>
                    <RegisteredIntegerField
                        label='Cirka belopp' // TODO should add currency in label
                        name='abbTarget'
                        disabled={!!offeredUnits}
                    />
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='secondaryOffering'>
                <FormTableRow label='Befintliga aktier som erbjuds (secondary)'>
                    <Stack direction='row' spacing={2}>
                        {!noSecondaryOfferingChecked && (
                            <RegisteredIntegerField
                                label='Antal aktier'
                                helperText='Utöver de som erbjuds i emissionen'
                                name='secondaryOffering'
                            />
                        )}
                        <RegisteredCheckBox
                            label='Inga befintliga aktier erbjuds'
                            name='noSecondaryOffering'
                            onChange={() => {
                                setIsCustomFormDirty(true)
                            }}
                        />
                    </Stack>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='noOverAllotment'>
                <FormTableRow label='Övertilldelning antal units'>
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'flex-start',
                        }}
                    >
                        {!noOverAllotmentChecked && (
                            <Stack spacing={4}>
                                {totalOfferedUnits !== 0 ? (
                                    <Stack spacing={0.5}>
                                        <RegisteredIntegerField
                                            label='Övertilldelning antal units'
                                            name='overAllotmentSize'
                                        />
                                        <FieldExtractionSuggestion
                                            value={suggestionOfferingTerms.overAllotmentSize}
                                            fieldValue={formMethods.watch('overAllotmentSize')}
                                            onUse={() =>
                                                applySuggestion(
                                                    'overAllotmentSize',
                                                    suggestionOfferingTerms.overAllotmentSize,
                                                )
                                            }
                                            provenance={suggestionProvenanceByField.overAllotmentSize}
                                        />
                                    </Stack>
                                ) : null}
                                {!noSecondaryOfferingChecked ? (
                                    <ShouldShow issueType={issue.type} issueField='overAllotmentSizeSecondary'>
                                        <RegisteredIntegerField
                                            label='Övertilldelning (secondary)'
                                            name='overAllotmentSizeSecondary'
                                        />
                                    </ShouldShow>
                                ) : null}
                            </Stack>
                        )}
                        <RegisteredCheckBox
                            label='Ingen övertilldelning'
                            name='noOverAllotment'
                            onChange={() => {
                                setIsCustomFormDirty(true)
                            }}
                        />
                    </Stack>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='minimumSubCondition'>
                <FormTableRow label='Villkorad teckningsgrad'>
                    <RegisteredNumericField
                        label='Teckningsgrad'
                        name='minimumSubCondition'
                        minDecimals={0}
                        maxDecimals={2}
                        percentage={true}
                    />
                </FormTableRow>
            </ShouldShow>
        </FormTable>
    )
}

export default TermsFields
